import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Space,
  Modal,
  Upload,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import type { Cuffs } from "../../../../types/Cuffs";

interface CuffModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: Cuffs | null;
  isEditMode?: boolean;
  cuffs: Cuffs[];
}

const CuffModal: React.FC<CuffModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
  cuffs,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);


  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

   // image preview 
  const handlePreview = async (file: UploadFile) => {
      if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj as File);
      }

      setPreviewImage(file.url || (file.preview as string));
      setPreviewOpen(true);
      setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  // Utility function to convert image to base64
  const getBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = error => reject(error);
      });

  const handleSubmit = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('cuff_name', values.cuff_name);

      if (imageFile) {
        formData.append('attachments', imageFile);
      }

      if (initialValues?.cuff_id) {
        formData.append('cuff_id', initialValues.cuff_id.toString());
      }

      const url = isEditMode ? 'edit-cuff' : 'add-cuff';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onCreate(data);
      showSuccess(isEditMode ? 'Cuff updated successfully!' : 'Cuff created successfully!');
      form.resetFields();
      if (!isModalClose) {
          setImageFile(null);
          setImageFileList([]);
      }

      if (isModalClose) {
        onCancel();
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} cuff`);
    }
  };

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        cuff_name: initialValues.cuff_name,
      });
      if (initialValues.cuff_image) {
        setImageFileList([
          {
            uid: '-1',
            name: 'image.png',
            status: 'done',
            url: initialValues.cuff_image,
          },
        ]);
        setImageFile(null);
      }
    } else if (visible) {
      form.resetFields();
      setImageFileList([]);
      setImageFile(null);
    }
  }, [visible, isEditMode, initialValues, form]);

  const uploadButton = (
    <div
      style={{
        width: 220,
        height: 220,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <PlusOutlined style={{ fontSize: 24 }} />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <>
      <Modal
        title={isEditMode ? 'Edit Cuff' : 'Add New Cuff'}
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width="90%"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cuff_name"
                className="px-8"
                label={
                  <span>
                    Cuff Name&nbsp;
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      (Max 100 characters)
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter the cuff name' },
                  { max: 100, message: 'Maximum 100 characters allowed' },
                ]}
              >
                <Input
                  showCount
                  maxLength={100}
                  placeholder="e.g., Barrel Cuff"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Upload Cuff Image" required>
                <Upload
                  beforeUpload={() => false}
                  listType="picture-card"
                  maxCount={1}
                  fileList={imageFileList}
                  onPreview={handlePreview} // 👈 Add this
                  onChange={({ fileList }) => {
                    setImageFileList(fileList);
                    const rawFile = fileList[0]?.originFileObj;
                    if (rawFile instanceof File) {
                      setImageFile(rawFile);
                    } else {
                      setImageFile(null);
                    }
                  }}
                >
                  {imageFileList.length >= 1 ? null : uploadButton}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={onCancel}>Cancel</Button>
              {!isEditMode && (
                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => setIsModalClose(false)}
                  disabled={isSubmitDisabled()}
                >
                  Submit & Create New Cuff
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(true)}
                disabled={isSubmitDisabled()}
              >
                Submit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
          visible={previewOpen}
          title={previewTitle}
          footer={null}
          centered
          onCancel={() => setPreviewOpen(false)}
      >
          <img
              alt="Preview"
              style={{ width: '100%' }}
              src={previewImage}
      />
      </Modal>
    </>
  );
};

export default CuffModal;
